import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'

import { AuthProvider } from '@/auth/AuthProvider'
import { TokenBridge } from '@/auth/TokenBridge'
import { AuthError, installSessionExpiredHandler } from '@/api/client'
import { RootErrorBoundary } from '@/components/RootErrorBoundary'
import { observeRouterPerf } from '@/lib/perf'
import { scrubBreadcrumb, scrubSentryEvent } from '@/lib/sentry-scrub'

import { routeTree } from './routeTree.gen'

import './index.css'

document.documentElement.classList.add('dark')

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const environmentName = import.meta.env.VITE_ENVIRONMENT_NAME ?? 'development'

// Sentry is opt-in: no DSN ⇒ no init ⇒ no events sent. ``Sentry.ErrorBoundary``
// + ``Sentry.captureException`` are still importable and run as no-ops, so the
// rest of the app stays env-agnostic.
if (sentryDsn && environmentName !== 'development' && environmentName !== 'test') {
  Sentry.init({
    dsn: sentryDsn,
    environment: environmentName,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1',
    ),
    integrations: [Sentry.browserTracingIntegration()],
    // Default-PII is OFF: Sentry will not auto-include IP, cookies, request
    // bodies, or user emails. The scrubber below catches anything that still
    // gets in via breadcrumbs / explicit captureException calls.
    sendDefaultPii: false,
    beforeSend: scrubSentryEvent,
    beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      // AuthError MUST bypass the generic retry/backoff loop. authFetch
      // already attempted exactly one coalesced refresh+replay; if the
      // bearer is still rejected, retrying with backoff just delays the
      // session-expired UX and burns mobile bandwidth.
      retry: (failureCount, error) => {
        if (error instanceof AuthError) return false
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    },
    mutations: {
      retry: (failureCount, error) => {
        if (error instanceof AuthError) return false
        return failureCount < 2
      },
    },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Lightweight perf profiling: log a structured route-transition entry on
// each navigation and flag any transition that breaches the documented
// p95 budget. Route ids only — no path params / PII. See
// ``frontend/src/lib/perf.ts`` and ``docs/PERF_BUDGETS.md``.
observeRouterPerf(router)

// When authFetch has tried-and-failed to refresh the bearer, route the user
// straight to /login instead of letting them watch React Query backoff into
// the same dead session over and over. The handler is rate-limited to one
// navigate per second so a burst of in-flight 401s doesn't flap the URL.
let lastSessionExpiredNav = 0
installSessionExpiredHandler(() => {
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/login') return
  const now = Date.now()
  if (now - lastSessionExpiredNav < 1000) return
  lastSessionExpiredNav = now
  // Drop any cached query data tied to the dead session so the next sign-in
  // refetches fresh, then navigate. Use the router (not window.location) so
  // we stay within the SPA and don't re-bootstrap Logto from scratch.
  queryClient.cancelQueries()
  queryClient.clear()
  router.navigate({ to: '/login' })
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TokenBridge>
            <RouterProvider router={router} />
          </TokenBridge>
        </AuthProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  </StrictMode>,
)
